import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, ChevronLeft, ChevronRight, AlertCircle, Filter } from 'lucide-react';
import { GameCard } from '../components/GameCard';
import { getRankedGames, SortOptions } from '../lib/game/searchService';
import { toggleOwned as toggleOwnedService, toggleWishlist as toggleWishlistService, togglePlayed as togglePlayedService } from '../lib/gameService';
import { useAuth } from '../contexts/AuthContext';
import type { Game, GameSearchFilters, UserGame } from '../types';
import { useLocation, Link, useSearchParams, useNavigate } from 'react-router-dom';
import GameFilters, { SortOption } from '../components/GameFilters';
import useDebounce from '../hooks/useDebounce';
import { rateGame } from '../lib/game/ratingService';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

// Categorias de jogos comuns (usadas para os filtros)
const GAME_CATEGORIES = [
  'Estratégia', 'Familiar', 'Party Game', 'Aventura', 'Cooperativo',
  'Deckbuilding', 'Euro', 'Temático', 'Tático', 'RPG',
  'Card Game', 'Educativo', 'Abstrato', 'Infantil', 'Competitivo'
];

const SearchPage: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [userCollectionIds, setUserCollectionIds] = useState<{ owned: Set<string>; wishlist: Set<string>; played: Set<string> }>({ owned: new Set(), wishlist: new Set(), played: new Set() });
  const location = useLocation();
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>('name');
  const [apiSortOptions, setApiSortOptions] = useState<SortOptions>({ sortBy: 'name', ascending: true });
  const [userRatings, setUserRatings] = useState<Map<string, number>>(new Map());
  
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 25; // Definido como uma constante conforme solicitado
  
  const debouncedSearchText = useDebounce(searchText, 500);

  // Filtros (estado imediato)
  const [filters, setFilters] = useState<GameSearchFilters>({
    searchText: undefined,
    categories: [],
    mechanics: [],
    themes: [],
    minPlayers: undefined,
    maxPlayers: undefined,
    maxPlayTime: undefined,
    minPlayTime: undefined,
    minComplexity: undefined,
    maxComplexity: undefined,
    year: undefined,
    minAge: undefined,
  });
  
  // Filtros Debounced (para acionar a busca)
  const debouncedFilters = useDebounce(filters, 500); // <<< DEBOUNCE NO OBJETO DE FILTROS

  // Variáveis para controlar o estado da busca
  const hasSearchTerm = debouncedSearchText.length >= 2;
  const hasActiveFilters = Object.entries(debouncedFilters).some(([key, val]) => {
    if (key === 'searchText') return false; // searchText é tratado por hasSearchTerm
    if (Array.isArray(val)) return val.length > 0;
    if (key === 'minAge') return typeof val === 'number' && val > 0;
    if (key === 'maxPlayers') return typeof val === 'number';
    return val !== undefined && val !== null && val !== '';
  });

  // Configurar ordenação por relevância quando o usuário digita na busca
  useEffect(() => {
    const shouldUseRelevance = debouncedSearchText.length >= 2 && !debouncedFilters.maxPlayers;

    if (shouldUseRelevance) {
      setApiSortOptions(prev => {
        if (prev.sortBy === 'relevance' && prev.searchRelevance === true) {
          return prev;
        }
        return {
          ...prev,
          sortBy: 'relevance',
          searchRelevance: true
        };
      });
    } else { // Se não deve usar relevância (sem texto, ou maxPlayers ativo com texto)
      setApiSortOptions(prev => {
        // Se estava em relevância, volta para 'name' ou o sortBy anterior.
        // Se não estava em relevância, mantém o sortBy atual.
        const newSortBy = prev.searchRelevance && prev.sortBy === 'relevance' ? 'name' : prev.sortBy;
        if (prev.sortBy === newSortBy && prev.searchRelevance === false) {
          return prev; // Nada mudou
        }
        return {
          ...prev,
          sortBy: newSortBy === 'relevance' ? 'name' : newSortBy, // Garante que não fique 'relevance' se searchRelevance for false
          searchRelevance: false
        };
      });
    }
  }, [debouncedSearchText, debouncedFilters.maxPlayers, apiSortOptions.sortBy, apiSortOptions.searchRelevance]); // Adicionado debouncedFilters.maxPlayers e apiSortOptions.sortBy

  // Buscar coleção de IDs do usuário ao carregar/autenticar
  useEffect(() => {
    const fetchUserCollectionIds = async () => {
      if (isAuthenticated && user) {
        try {
          // Idealmente usar getUserCollectionStatusIds de collectionService quando disponível
          const ownedIds = new Set<string>();
          const wishlistIds = new Set<string>();
          const playedIds = new Set<string>();
          const ratings = new Map<string, number>();
          
          // Buscar ratings dos jogos do usuário
          const { data: userGames, error } = await supabase
            .from('user_games')
            .select('game_id, rating')
            .eq('user_id', user.id)
            .not('rating', 'is', null);
            
          if (!error && userGames) {
            userGames.forEach(game => {
              if (game.rating) {
                ratings.set(game.game_id, game.rating);
              }
            });
          }
          
          setUserRatings(ratings);
          setUserCollectionIds({ owned: ownedIds, wishlist: wishlistIds, played: playedIds });
        } catch (error) {
          // console.error('Erro ao buscar IDs da coleção do usuário:', error); // Removido
        }
      } else {
        setUserCollectionIds({ owned: new Set(), wishlist: new Set(), played: new Set() });
        setUserRatings(new Map());
      }
    };

    if (!authLoading) {
      fetchUserCollectionIds();
    }
  }, [user, isAuthenticated, authLoading]);

  // handleSortChange atualiza apiSortOptions (sem debounce aqui, mudança de sort é única)
  const handleSortChange = (newSortOption: SortOption) => {
    setSelectedSortOption(newSortOption);

    let newApiSort: SortOptions;
    switch (newSortOption) {
      case 'name':
        newApiSort = { sortBy: 'name', ascending: true };
        break;
      case 'rating_desc':
        newApiSort = { sortBy: 'average_rating', ascending: false };
        break;
      case 'year_desc':
        newApiSort = { sortBy: 'year', ascending: false };
        break;
      case 'complexity_asc':
        newApiSort = { sortBy: 'average_complexity', ascending: true };
        break;
      case 'complexity_desc':
        newApiSort = { sortBy: 'average_complexity', ascending: false };
        break;
      default:
        newApiSort = { sortBy: 'name', ascending: true };
    }

    setApiSortOptions(newApiSort);
    setCurrentPage(1);
  };

  // Buscar jogos quando o texto DEBOUNCED, filtros DEBOUNCED, ordenação ou página mudarem
  const performSearch = useCallback(async () => {
    // Usar hasSearchTerm e hasActiveFilters definidas no escopo do componente
    if (!hasSearchTerm && !hasActiveFilters) {
      setSearchResults([]);
      setTotalPages(1);
      setTotalItems(0);
      return;
    }

    setLoading(true);
    try {
      // Usar debouncedFilters na construção do objeto para a API
      const searchFilters: GameSearchFilters = {
        ...debouncedFilters,
        searchText: hasSearchTerm ? debouncedSearchText : undefined,
        minAge: debouncedFilters.minAge,
      };

      const response = await getRankedGames(apiSortOptions, searchFilters, currentPage, pageSize);
      setSearchResults(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.totalItems);
    } catch (error) {
      console.error('Erro ao buscar jogos:', error);
      setSearchResults([]);
      setTotalPages(1);
      setTotalItems(0);
      // Adicionar mensagem de erro para o usuário?
    } finally {
      setLoading(false);
    }
  }, [hasSearchTerm, hasActiveFilters, debouncedSearchText, debouncedFilters, apiSortOptions, currentPage, pageSize]);

  // Reduzir chamadas desnecessárias e adicionar condição para evitar múltiplas chamadas
  useEffect(() => {
    // Implementar uma flag para evitar chamadas em cascata
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [performSearch]); // Depender dos valores debounced e outros que acionam busca

  // Usar funções de serviço corretas
  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user?.id) return;
    try {
      await toggleWishlistService(user.id, gameId, isWishlist);
      setUserCollectionIds(prev => {
        const newSet = new Set(prev.wishlist);
        if (isWishlist) newSet.add(gameId); else newSet.delete(gameId);
        return { ...prev, wishlist: newSet }; // Retornar o novo Set
      });
    } catch (error) {
      // console.error('Erro ao atualizar wishlist:', error); // Removido
    }
  };

  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user?.id) return;
    try {
      await toggleOwnedService(user.id, gameId, isOwned);
      setUserCollectionIds(prev => {
        const newSet = new Set(prev.owned);
        if (isOwned) newSet.add(gameId); else newSet.delete(gameId);
        return { ...prev, owned: newSet }; // Retornar o novo Set
      });
    } catch (error) {
      // console.error('Erro ao atualizar coleção:', error); // Removido
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user?.id) return;
    try {
      await togglePlayedService(user.id, gameId, isPlayed);
      setUserCollectionIds(prev => {
        const newSet = new Set(prev.played);
        if (isPlayed) newSet.add(gameId); else newSet.delete(gameId);
        return { ...prev, played: newSet }; // Retornar o novo Set
      });
    } catch (error) {
      // console.error('Erro ao marcar jogo como jogado:', error); // Removido
    }
  };

  // handleFilterChange atualiza o estado IMEDIATO
  const handleFilterChange = (newFilters: Partial<GameSearchFilters>) => {
    setFilters((prevFilters: GameSearchFilters) => ({ ...prevFilters, ...newFilters }));
    setCurrentPage(1); // Resetar página ao mudar filtros
  };

  // clearFilters atualiza o estado IMEDIATO
  const clearFilters = () => {
    setFilters({
      searchText: undefined,
      categories: [],
      mechanics: [],
      themes: [],
      minPlayers: undefined,
      maxPlayers: undefined,
      maxPlayTime: undefined,
      minPlayTime: undefined,
      minComplexity: undefined,
      maxComplexity: undefined,
      year: undefined,
      minAge: undefined,
    });
    setCurrentPage(1);
  };

  // handleSearchInputChange atualiza o estado IMEDIATO
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  // Manipuladores de paginação
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prevPage => prevPage - 1);
    }
  };

  // Renderizar controles de paginação
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        <button
          onClick={goToPrevPage}
          disabled={currentPage === 1}
          className={`flex items-center px-3 py-1 rounded ${
            currentPage === 1 
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }`}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </button>
        
        <span className="text-gray-700">
          Página {currentPage} de {totalPages} ({totalItems} jogos)
        </span>
        
        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          className={`flex items-center px-3 py-1 rounded ${
            currentPage === totalPages 
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }`}
        >
          Próxima
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    );
  };

  // Adicionar função para avaliar jogos
  const handleRateGame = async (gameId: string, rating: number) => {
    if (!user?.id) return;
    try {
      await rateGame(user.id, gameId, rating);
      setUserRatings(prev => {
        const newRatings = new Map(prev);
        newRatings.set(gameId, rating);
        return newRatings;
      });
    } catch (error) {
      console.error('Erro ao avaliar jogo:', error);
    }
  };

  return (
    <>
      <SEO 
        title="Buscar Jogos"
        description="Encontre jogos de tabuleiro para sua coleção usando nossa ferramenta de busca."
      />
      <div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
        {/* Notificação */}
        {notificationMessage && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative mb-6" role="alert">
            <span className="block sm:inline">{notificationMessage}</span>
          </div>
        )}

        {/* Barra de Busca e Botão de Filtro (sempre visível) */}
        <div className="mb-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="relative flex-grow w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={searchText}
              onChange={handleSearchInputChange}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-indigo-400 dark:focus:border-indigo-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)} // Controla visibilidade da seção abaixo
            className="flex-shrink-0 p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 w-full sm:w-auto flex items-center justify-center gap-2 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
            aria-label="Mostrar/Esconder Filtros"
          >
            <Filter className="h-5 w-5" />
            <span>Filtros {showFilters ? '(Visíveis)' : ''}</span>
          </button>
        </div>

        {/* Seção de Filtros (controlada por showFilters) */}
        {showFilters && (
          <div className="mb-6 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg shadow overflow-x-auto max-w-full dark:border dark:border-gray-700">
            <GameFilters
              filters={filters} // <<< Passar filtros IMEDIATOS para UI do GameFilters
              showFilters={true} 
              sortBy={selectedSortOption}
              setShowFilters={setShowFilters}
              onFilterChange={handleFilterChange} // <<< Atualiza filtros IMEDIATOS
              onSortChange={handleSortChange}
              onClearFilters={clearFilters} // <<< Atualiza filtros IMEDIATOS
            />
          </div>
        )}

        {/* Área de Resultados */}
        <div className="mt-6"> {/* Adicionado margem superior */}
          {loading ? (
            <div className="text-center py-10">
              <p className="text-gray-600 dark:text-gray-400">Buscando jogos...</p>
            </div>
          ) : !hasSearchTerm && !hasActiveFilters ? (
            <div className="text-center py-16 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">Pronto para encontrar jogos?</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Digite um termo na barra de busca (mínimo 2 caracteres) ou aplique filtros para começar.
              </p>
            </div>
          ) : searchResults.length > 0 ? (
            <>
              {/* Grid de resultados */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {searchResults.map(game => (
                  <GameCard
                    key={game.id}
                    game={game}
                    userGame={{
                      id: `${user?.id || ''}-${game.id}`,
                      user_id: user?.id || '',
                      game_id: game.id,
                      owned: userCollectionIds.owned.has(game.id),
                      wishlist: userCollectionIds.wishlist.has(game.id),
                      play_count: 0,
                      rating: userRatings.get(game.id) || 0,
                      complexity_rating: null,
                      notes: null,
                      created_at: '',
                      updated_at: '',
                      game: game
                    }}
                    linkTo={game.slug ? `/games/${game.slug}` : `/games/${game.id}`}
                    isInitiallyOwned={userCollectionIds.owned.has(game.id)}
                    isInitiallyWishlist={userCollectionIds.wishlist.has(game.id)}
                    isInitiallyPlayed={userCollectionIds.played.has(game.id)}
                    onToggleOwned={handleToggleOwned}
                    onToggleWishlist={handleToggleWishlist}
                    onTogglePlayed={handleTogglePlayed}
                    onRate={handleRateGame}
                    isInCollection={userCollectionIds.owned.has(game.id)}
                    user={user}
                  />
                ))}
              </div>
              {/* Paginação */}
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-16 bg-gray-100 dark:bg-gray-800 rounded-lg">
               <AlertCircle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">Nenhum jogo encontrado</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Tente ajustar seus filtros ou termo de busca.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage; 